import { useEffect, useState } from 'react';
import { widgetInstances } from '../../services/widgetApi';
import { describeApiError } from '../../services/apiError';

const OFF = { state: 'off', feed: null, instance: null, error: null };
const LOADING = { ...OFF, state: 'loading' };
const MARGIN_MS = 60_000;
const RETRY_MS = 60_000;
const MAX_RETRIES = 2;
const MAX_TIMEOUT_MS = 2_147_483_647;
const sameScope = (a, b) => a?.widgetId === b.widgetId && a?.instanceId === b.instanceId;

/** Earliest Google signed-media expiry, including nested image fields. */
export function signedMediaExpiry(value) {
  let earliest = Infinity;
  const seen = new Set();
  const visit = (v) => {
    if (typeof v === 'string' && /^https?:\/\//.test(v)) {
      try {
        const q = new URL(v).searchParams;
        const date = q.get('X-Goog-Date');
        const seconds = Number(q.get('X-Goog-Expires'));
        if (!/^\d{8}T\d{6}Z$/.test(date || '') || !Number.isFinite(seconds) || seconds <= 0) return;
        const iso = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${date.slice(9, 11)}:${date.slice(11, 13)}:${date.slice(13, 15)}Z`;
        const expiry = Date.parse(iso) + seconds * 1000;
        if (Number.isFinite(expiry)) earliest = Math.min(earliest, expiry);
      } catch { /* Not a URL the media lifecycle can renew. */ }
    } else if (v && typeof v === 'object' && !seen.has(v)) {
      seen.add(v);
      Object.values(v).forEach(visit);
    }
  };
  visit(value);
  return Number.isFinite(earliest) ? earliest : null;
}

/**
 * Shared native user-feed lifecycle. Signed URLs and API contracts stay the
 * same; only their read-time lifetime is renewed. Refreshes keep existing rows
 * and component state. Auth logout unmounts the host; scope switches and cleanup
 * abort requests so another widget/account cannot receive a late response.
 */
export default function useWidgetUserFeed(enabled, widgetId, instanceId = null, reloadKey = 0) {
  const [live, setLive] = useState(OFF);

  useEffect(() => {
    if (!enabled || !widgetId) { setLive(OFF); return undefined; }
    const scope = { widgetId, instanceId };
    let disposed = false;
    let inFlight = null;
    let controller = null;
    let timer = null;
    let refreshAt = Infinity;
    let failures = 0;
    let staleRenewals = 0;
    let inst = instanceId;
    let settings = {};

    const visible = () => globalThis.document?.visibilityState !== 'hidden';
    const clearTimer = () => { clearTimeout(timer); timer = null; };
    const schedule = () => {
      clearTimer();
      if (disposed || !Number.isFinite(refreshAt)) return;
      timer = setTimeout(() => {
        if (!visible()) return;
        if (Date.now() >= refreshAt) refresh();
        else schedule();
      }, Math.min(MAX_TIMEOUT_MS, Math.max(0, refreshAt - Date.now())));
    };

    const refresh = () => {
      if (disposed || inFlight) return;
      clearTimer();
      controller = new AbortController();
      const signal = controller.signal;
      setLive((prev) => sameScope(prev.scope, scope) && prev.feed
        ? { ...prev, error: null }
        : { ...LOADING, scope });
      inFlight = (async () => {
        try {
          if (!inst) {
            const list = await widgetInstances.list({ signal });
            if (disposed || signal.aborted) return;
            const rows = Array.isArray(list) ? list : (list?.instances || []);
            const mine = rows.find((i) => i.widget_id === widgetId);
            inst = mine?.instance_id || null;
            settings = mine?.settings || {};
          }
          if (!inst) {
            refreshAt = Infinity;
            setLive({ ...OFF, scope, state: 'not-adopted' });
            return;
          }
          const res = await widgetInstances.data(inst, { limit: 100, format: 'signed', signal });
          if (disposed || signal.aborted) return;
          const entries = Array.isArray(res) ? res : (res?.entries || []);
          setLive({
            scope, state: 'ready', instance: inst, error: null,
            feed: { widget_id: widgetId, instance_id: inst, entries, settings, pending: { adds: [], updates: [] } },
          });
          failures = 0;
          const expires = signedMediaExpiry(entries);
          if (expires === null) { refreshAt = Infinity; return; }
          // A cache returning the same near-expired URL must not create a hot
          // fetch loop. Stop automatic retries after two; foreground retries
          // remain rate-limited by refreshAt.
          staleRenewals = expires - MARGIN_MS <= Date.now() ? staleRenewals + 1 : 0;
          refreshAt = Math.max(expires - MARGIN_MS, Date.now() + RETRY_MS);
          if (staleRenewals <= MAX_RETRIES) schedule();
        } catch (error) {
          if (disposed || signal.aborted || error?.name === 'AbortError') return;
          const revoked = [401, 403, 404].includes(Number(error?.status));
          setLive((prev) => !revoked && sameScope(prev.scope, scope) && prev.feed
            ? { ...prev, error: describeApiError(error) }
            : { ...OFF, scope, state: 'error', error: describeApiError(error) });
          refreshAt = revoked ? Infinity : Date.now() + RETRY_MS;
          failures += 1;
          if (failures <= MAX_RETRIES) schedule();
        } finally {
          inFlight = null;
        }
      })();
    };

    const onActive = () => { if (visible() && Date.now() >= refreshAt) refresh(); };
    globalThis.addEventListener?.('focus', onActive);
    globalThis.addEventListener?.('online', onActive);
    globalThis.document?.addEventListener('visibilitychange', onActive);
    refresh();
    return () => {
      disposed = true;
      controller?.abort();
      clearTimer();
      globalThis.removeEventListener?.('focus', onActive);
      globalThis.removeEventListener?.('online', onActive);
      globalThis.document?.removeEventListener('visibilitychange', onActive);
    };
  }, [enabled, widgetId, instanceId, reloadKey]);

  if (!enabled || !widgetId) return OFF;
  // Effects run after render: never expose the previous widget's feed even for
  // the one render before a new scoped request starts.
  return sameScope(live.scope, { widgetId, instanceId }) ? live : LOADING;
}
